/**
 * L229-WEB-2 — reporter page `/support/my`.
 *
 * Every signed-in user (any role) reaches this from the avatar-dropdown
 * "Support" entry. Shows MY tickets (status + resolution visible) + a
 * "New ticket" modal: category toggle (Bug / Feature request), module
 * dropdown (catalog verbatim), 3-severity picker with spec-§1 helper text
 * VERBATIM, subject, description, optional image attachment(s) → base64
 * dataUrl (≤3, ≤2 MB each — server 400s on violation, we pre-validate).
 *
 * The backend auto-scopes the list to own-only for non-desk roles; for desk
 * roles (who receive ALL tickets from the same endpoint) we filter to
 * createdById === me client-side so this page stays "my tickets" for
 * everyone. `createdById` is stamped SERVER-side — we never send it.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Loader2, LifeBuoy, AlertCircle, ImagePlus, Trash2,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import {
  CATEGORY_OPTIONS, SEVERITY_OPTIONS, SUPPORT_MODULES, moduleLabel,
  MAX_ATTACHMENTS, type SupportAttachment,
} from './supportConstants';
import { attachmentError, fileToDataUrl } from './attachmentUtils';

interface MyTicket {
  id: string;
  number: string;
  subject: string;
  body?: string | null;
  status: 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category?: 'bug' | 'feature_request' | null;
  module?: string | null;
  resolution?: string | null;
  createdById?: string | null;
  createdAt: string;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
  waiting_customer: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  medium: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  high: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  critical: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
};

const EMPTY_FORM = {
  category: 'bug' as 'bug' | 'feature_request',
  module: '',
  priority: 'medium' as 'high' | 'medium' | 'low',
  subject: '',
  body: '',
};

export function MySupportPage() {
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachments, setAttachments] = useState<SupportAttachment[]>([]);
  const [formErr, setFormErr] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/support/tickets');
      const data = res.data.data?.tickets ?? res.data.data ?? [];
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load your tickets';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Desk roles receive ALL tickets from the same endpoint — keep this page
  // strictly "mine". Only tickets whose createdById matches the signed-in user
  // show here; legacy/null-createdById rows never leak onto anyone's reporter
  // page (non-desk users are already own-only server-side, so this is a no-op
  // for them and the correct scope for desk users).
  const mine = tickets.filter(
    (t) => !!user?.id && t.createdById === user.id,
  );

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setFormErr(null);
    let next = [...attachments];
    for (const file of Array.from(files)) {
      const err = attachmentError(
        { name: file.name, type: file.type, size: file.size },
        next.length,
      );
      if (err) { setFormErr(err); break; }
      try {
        const dataUrl = await fileToDataUrl(file);
        next = [...next, { name: file.name, dataUrl }];
      } catch {
        setFormErr(`Could not read ${file.name}.`);
        break;
      }
    }
    setAttachments(next);
  };

  const handleCreate = async () => {
    if (!form.subject.trim()) { setFormErr('Subject is required.'); return; }
    if (!form.body.trim()) { setFormErr('Description is required.'); return; }
    if (!form.module) { setFormErr('Pick the module this is about.'); return; }
    setSaving(true);
    setFormErr(null);
    try {
      await api.post('/support/tickets', {
        subject: form.subject.trim(),
        body: form.body.trim(),
        priority: form.priority,
        category: form.category,
        module: form.module,
        source: 'web',
        ...(attachments.length > 0 ? { attachments } : {}),
      });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setAttachments([]);
      fetchTickets();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to create ticket';
      setFormErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Support</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Report a bug or request a feature. Track what you've raised.
          </p>
        </div>
        <button
          data-tour="support-new-ticket"
          onClick={() => { setFormErr(null); setShowCreate(true); }}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} />
          New ticket
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} /> {error}
        </div>
      ) : mine.length === 0 ? (
        <div data-tour="support-tickets" className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16 text-gray-500 dark:border-gray-700 dark:bg-gray-900">
          <LifeBuoy size={32} className="mb-2 text-gray-300" />
          <div className="text-sm">Nothing raised yet</div>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-2 text-sm font-medium text-[var(--ink-0)] hover:underline"
          >
            Report a bug or request a feature
          </button>
        </div>
      ) : (
        <div data-tour="support-tickets" className="space-y-3">
          {mine.map((t) => (
            <div
              key={t.id}
              className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-gray-500">{t.number}</span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[t.status] || STATUS_STYLES.open}`}>
                  {t.status.replace(/_/g, ' ')}
                </span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${SEVERITY_STYLES[t.priority] || SEVERITY_STYLES.medium}`}>
                  {t.priority}
                </span>
                {t.category && (
                  <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                    {t.category === 'feature_request' ? 'Feature request' : 'Bug'}
                  </span>
                )}
                {t.module && (
                  <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    {moduleLabel(t.module)}
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-400">{formatDate(t.createdAt)}</span>
              </div>
              <div className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t.subject}</div>
              {t.resolution && (
                <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2.5 text-xs text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300">
                  <span className="font-semibold">Resolution: </span>
                  <span className="whitespace-pre-wrap">{t.resolution}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New ticket modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New ticket</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Category toggle */}
              <div data-tour="support-category">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">What is this?</label>
                <div className="flex gap-2">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        form.category === c.value
                          ? 'border-[var(--ink-0)] bg-[var(--ink-0)] text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Module */}
              <div data-tour="support-module">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Module *</label>
                <select
                  value={form.module}
                  onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">Where did this happen?</option>
                  {SUPPORT_MODULES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Severity — helper text is spec-§1 canonical, verbatim */}
              <div data-tour="support-severity">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Severity</label>
                <div className="space-y-1.5">
                  {SEVERITY_OPTIONS.map((s) => (
                    <label
                      key={s.value}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors ${
                        form.priority === s.value
                          ? 'border-[var(--ink-0)] bg-gray-50 dark:bg-gray-800'
                          : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                      }`}
                    >
                      <input
                        type="radio"
                        name="severity"
                        checked={form.priority === s.value}
                        onChange={() => setForm((f) => ({ ...f, priority: s.value }))}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{s.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">{s.helper}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div data-tour="support-subject">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Subject *</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* Description */}
              <div data-tour="support-description">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description *</label>
                <textarea
                  rows={4}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder={form.category === 'bug'
                    ? 'What happened? What did you expect?'
                    : 'What would you like AURA to do?'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Screenshots <span className="font-normal text-gray-400">(optional, up to {MAX_ATTACHMENTS} images, 2 MB each)</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {attachments.map((a, i) => (
                    <div key={i} className="relative">
                      <img
                        src={a.dataUrl}
                        alt={a.name}
                        className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                      />
                      <button
                        type="button"
                        onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white"
                        aria-label={`Remove ${a.name}`}
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  ))}
                  {attachments.length < MAX_ATTACHMENTS && (
                    <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 dark:border-gray-700">
                      <ImagePlus size={18} />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {formErr && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  <AlertCircle size={14} /> {formErr}
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                data-tour="support-submit"
                onClick={handleCreate}
                disabled={saving || !form.subject.trim() || !form.body.trim() || !form.module}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
